import { ref } from "vue";
import httpService from "@/services/http.service";
import storageService from "@/services/storage.service";
import router from "@/router/router";
import { isAccountBannedError } from "@/errors/AccountBannedError";
import { isAccountDeletedError } from "@/errors/AccountDeletedError";
import {
  clearAccountBannedSession,
  markAccountBannedSession,
} from "@/helpers/accountBannedSession";
import {
  clearAccountDeletedSession,
  markAccountDeletedSession,
  isAccountDeletedSession,
} from "@/helpers/accountDeletedSession";
import { isAccountBannedSession } from "@/helpers/accountBannedSession";
import {
  getKeycloak,
  initKeycloakSession,
  registerKeycloakTokenSink,
  setAuthCookie,
} from "@/lib/keycloak";
import { hasKeycloakConfig } from "@/lib/keycloak/config";
import { isSkipAutoKeycloakLoginPending } from "@/services/keycloak.service";

let initPromise: Promise<void> | null = null;

const getTokenClaim = (
  token: Record<string, unknown> | undefined,
  key: string
): string | undefined => {
  const value = token?.[key];
  return typeof value === "string" ? value : undefined;
};

const buildFallbackUser = async (
  kc: NonNullable<ReturnType<typeof getKeycloak>>
) => {
  const tokenParsed = kc.tokenParsed as Record<string, unknown> | undefined;
  let profile: any = null;
  try {
    profile = await kc.loadUserProfile();
  } catch {
    profile = null;
  }

  const fullNameFromProfile = [profile?.firstName, profile?.lastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  const fullName =
    fullNameFromProfile ||
    getTokenClaim(tokenParsed, "name") ||
    getTokenClaim(tokenParsed, "preferred_username") ||
    "";
  const userName =
    profile?.username ||
    getTokenClaim(tokenParsed, "preferred_username") ||
    getTokenClaim(tokenParsed, "email") ||
    "";
  const email = profile?.email || getTokenClaim(tokenParsed, "email") || "";
  const userId = getTokenClaim(tokenParsed, "sub") || "";

  return {
    _id: userId,
    fullName,
    userName,
    email,
    isProfileCompleted: true,
  };
};

const loadCurrentUser = async () => {
  try {
    const response = await httpService.getCurrentUser();
    const user =
      response?.data?.user ??
      response?.data ??
      response?.user ??
      response;
    if (user && user._id) return user;
  } catch (error: unknown) {
    if (isAccountBannedError(error) || isAccountDeletedError(error)) {
      throw error;
    }
    console.warn("[keycloak] failed to load current user:", error);
  }
  return null;
};

export type KeycloakSyncResult = "synced" | "deleted" | "banned" | "skipped";

export async function syncKeycloakAuth(
  kc: NonNullable<ReturnType<typeof getKeycloak>>
): Promise<KeycloakSyncResult> {
  if (!kc.token) return "skipped";

  storageService.setToken(kc.token);
  storageService.setIsFirebaseLogin(false);
  setAuthCookie(kc.token);

  let apiUser = null;
  try {
    apiUser = await loadCurrentUser();
  } catch (e: unknown) {
    if (isAccountBannedError(e)) {
      markAccountBannedSession();
      clearAccountDeletedSession();
      storageService.RemoveAllProperties();
      if (router.currentRoute.value.name !== "accountBanned") {
        await router.replace({ name: "accountBanned" });
      }
      return "banned";
    }
    if (isAccountDeletedError(e)) {
      markAccountDeletedSession();
      clearAccountBannedSession();
      storageService.RemoveAllProperties();
      if (router.currentRoute.value.name !== "accountDeleted") {
        await router.replace({ name: "accountDeleted" });
      }
      return "deleted";
    }
    throw e;
  }
  if (apiUser) {
    clearAccountBannedSession();
    clearAccountDeletedSession();
    storageService.setUser(apiUser);
    storageService.setBackground(apiUser);
    if (apiUser.sessionId) {
      storageService.setSessionId(apiUser.sessionId);
    }
    return "synced";
  }

  const fallbackUser: any = await buildFallbackUser(kc);
  storageService.setUser(fallbackUser);
  storageService.setBackground(fallbackUser);
  return "synced";
}

/**
 * Keycloak init and auth sync – safe to use in Vue components.
 */
export function resetKeycloakAuthInit(): void {
  initPromise = null;
}

export async function initKeycloakAuth(): Promise<void> {
  if (typeof window === "undefined") return;
  if (!hasKeycloakConfig()) return;
  if (isSkipAutoKeycloakLoginPending()) return;
  if (isAccountDeletedSession() || isAccountBannedSession()) return;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    registerKeycloakTokenSink((token) => storageService.setToken(token));

    const kc = await initKeycloakSession();
    if (!kc) return;

    if (kc.authenticated) {
      await syncKeycloakAuth(kc);
    }
  })();

  return initPromise;
}

export function useKeycloakAuthState() {
  const parsedLogin = ref(false);
  return { parsedLogin };
}

